/**
 * AeoSourceBadge
 *
 * A compact, reusable badge that signals to visitors (and reinforces to AI
 * crawlers reading the DOM) that this profile / page is structured as an
 * AEO-ready source — meaning it is formatted so AI answer engines like
 * Perplexity, ChatGPT, and Google Gemini can retrieve and cite it.
 *
 * Two tiers driven by the existing profileSeoScore:
 *   score >= 60  → "AEO Enhanced" (fully optimised — has description, services, photo, trust)
 *   score >= 1   → "AEO Ready"    (structure is present, Google and AI can index it)
 *   score === 0  → badge not shown (caller should conditionally render)
 *
 * Usage:
 *   <AeoSourceBadge />                        — default compact pill (AEO Ready)
 *   <AeoSourceBadge score={75} />             — pill auto-selects tier from score
 *   <AeoSourceBadge variant="inline" />        — single-line text badge for trust bars
 *   <AeoSourceBadge variant="card" score={82}/>— larger card for pricing / feature sections
 *   <AeoSourceBadge variant="profile" score={45}/> — profile-page block with plain-English first
 */

import React from 'react';

type AeoSourceBadgeVariant = 'pill' | 'inline' | 'card' | 'profile';

interface AeoSourceBadgeProps {
  variant?: AeoSourceBadgeVariant;
  /** profileSeoScore total (0–100). Drives tier: ≥60 = Enhanced, ≥1 = Ready. */
  score?: number;
  className?: string;
}

// Microdata-friendly SVG spark icon — no external dependency
function SparkIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8 1.5L9.5 6H14L10.25 8.75L11.75 13.25L8 10.5L4.25 13.25L5.75 8.75L2 6H6.5L8 1.5Z"
        fill="currentColor"
      />
    </svg>
  );
}

export function AeoSourceBadge({ variant = 'pill', score, className = '' }: AeoSourceBadgeProps) {
  // Derive tier from score. No score prop = assume Ready (used for non-profile pages).
  const isEnhanced = typeof score === 'number' && score >= 60;
  const tierLabel = isEnhanced ? 'AEO Enhanced' : 'AEO Ready';
  const tierTitle = isEnhanced
    ? 'This profile is fully optimised for AI citation — it has the description, services, photo, and trust signals AI answer engines need to recommend it with confidence.'
    : 'This profile is structured as an AEO-ready source — formatted so AI answer engines can retrieve and index it.';

  if (variant === 'profile') {
    return (
      <div
        className={`rounded-xl border ${
          isEnhanced ? 'border-[#c8d8f8] bg-[#eef4ff]' : 'border-[#d7deef] bg-[#f4f7fd]'
        } px-4 py-3 ${className}`}
        aria-label={tierTitle}
      >
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold text-slate-700">
              Your profile is structured to be cited by AI
            </p>
            <p className="mt-0.5 text-xs text-slate-500">
              {isEnhanced
                ? 'Google Gemini, Perplexity and ChatGPT can retrieve and recommend this profile.'
                : 'This profile is indexed and readable by AI answer engines.'}
            </p>
          </div>
          <span
            className={`shrink-0 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${
              isEnhanced
                ? 'bg-[#001489] text-white'
                : 'bg-[#e8eefb] text-[#001489] border border-[#c5d2ef]'
            }`}
            title={tierTitle}
          >
            <SparkIcon size={11} />
            {tierLabel}
          </span>
        </div>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <span
        className={`inline-flex items-center gap-1 text-xs font-semibold text-[#001489] ${className}`}
        title={tierTitle}
      >
        <SparkIcon size={12} />
        {tierLabel} — Answer Engine Optimised
      </span>
    );
  }

  if (variant === 'card') {
    return (
      <div
        className={`rounded-2xl border ${
          isEnhanced ? 'border-[#c8d8f8] bg-[#eef4ff]' : 'border-[#d7deef] bg-[#f4f7fd]'
        } px-5 py-4 ${className}`}
        aria-label={`${tierLabel} — Answer Engine Optimisation`}
      >
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#001489] text-white">
            <SparkIcon size={18} />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#214186]">
              {tierLabel} — Answer Engine Optimised
            </p>
            <p className="mt-1 text-sm font-semibold text-[#00004c]">
              {isEnhanced ? 'Fully optimised to be cited by AI' : 'Structured to be cited by AI'}
            </p>
            <p className="mt-1 text-sm leading-relaxed text-slate-600">
              {isEnhanced
                ? 'This profile has the description, services, trust signals, and location context that AI answer engines — including Google Gemini, Perplexity, and ChatGPT — need to recommend it with confidence.'
                : 'This profile is formatted so AI answer engines — including Google Gemini, Perplexity, and ChatGPT — can retrieve and recommend it in response to real customer searches.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Default: pill
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${
        isEnhanced
          ? 'border-[#001489] bg-[#001489] text-white'
          : 'border-[#d0d9f5] bg-[#f0f4ff] text-[#001489]'
      } ${className}`}
      title={tierTitle}
      aria-label={`${tierLabel} — Answer Engine Optimisation`}
    >
      <SparkIcon size={12} />
      {tierLabel}
    </span>
  );
}
